// PWAInstallBanner — subtil banner som viser "Installer app"-CTA
// - Skjules automatisk hvis brukeren allerede kjører som PWA
// - Android/desktop: bruker native beforeinstallprompt
// - iOS Safari: viser instruksjon (Del → Legg til på Hjem-skjerm)
// - Kan avvises av brukeren (huskes 14 dager i localStorage)

import React, { useState, useEffect } from "react";
import { Download, X, Share } from "lucide-react";
import { useIsPWA, useInstallPrompt, useIsIOSMobile } from "../hooks/usePWA";
import { useAuth } from "../contexts/AuthContext";

const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_DAYS = 14;

export default function PWAInstallBanner() {
  const { user } = useAuth();
  const isPWA = useIsPWA();
  const { canInstall, promptInstall } = useInstallPrompt();
  const isIOS = useIsIOSMobile();
  const [dismissed, setDismissed] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (ts && Date.now() - ts < DISMISS_DAYS * 24 * 3600 * 1000) {
        setDismissed(true);
      }
    } catch { /* ignorer */ }
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignorer */ }
  };

  // MIDLERTIDIG: kun tilgjengelig for eier under testing
  const isOwner = user?.role === "owner";
  if (!isOwner) return null;

  if (isPWA || dismissed) return null;

  // Kun vis på mobil (touch + max viewport 900px). Skjul på desktop/web.
  const isMobileViewport = typeof window !== "undefined"
    && window.matchMedia && window.matchMedia("(max-width: 900px), (pointer: coarse)").matches;
  if (!isMobileViewport && !isIOS) return null;

  // Vis kun hvis vi enten kan installere (Android/mobil-desktop) eller er på iOS Safari
  if (!canInstall && !isIOS) return null;

  const handleClick = async () => {
    if (canInstall) {
      const res = await promptInstall();
      if (res.outcome === "accepted") setDismissed(true);
    } else if (isIOS) {
      setShowIOSHint(true);
    }
  };

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-40 bg-[var(--noir)] border-t border-[var(--magenta)]/40 shadow-2xl"
        data-testid="pwa-install-banner"
      >
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="shrink-0 w-10 h-10 border border-[var(--magenta)]/40 flex items-center justify-center">
            <Download className="w-4 h-4 text-[var(--magenta)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-meta text-xs tracking-[0.15em] text-[var(--magenta)]">
              INSTALLER APP
            </div>
            <div className="text-sm text-[var(--cream)] truncate">
              Legg til Norsk Erotikk på hjemskjermen — raskere tilgang, ingen nettleser-UI.
            </div>
          </div>
          <button
            onClick={handleClick}
            className="shrink-0 bg-[var(--magenta)] text-white px-4 py-2 text-xs font-meta tracking-[0.15em] hover:opacity-90 transition"
            data-testid="pwa-install-button"
          >
            {isIOS ? "SLIK" : "INSTALLER"}
          </button>
          <button
            onClick={dismiss}
            aria-label="Lukk"
            className="shrink-0 text-[var(--cream-faint)] hover:text-[var(--cream)] p-1"
            data-testid="pwa-install-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showIOSHint && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOSHint(false)}
        >
          <div
            className="bg-[var(--noir)] border border-white/10 max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-meta text-xs tracking-[0.2em] text-[var(--magenta)] mb-2">
              INSTALLASJON PÅ IPHONE
            </div>
            <div className="font-editorial text-xl leading-tight mb-4">
              Legg til Norsk Erotikk på Hjem-skjerm
            </div>
            <ol className="space-y-3 text-sm text-[var(--cream-soft)]">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 border border-[var(--magenta)]/50 flex items-center justify-center text-[var(--magenta)] text-xs">1</span>
                <span>Trykk på <Share className="inline w-4 h-4 mx-1 text-[var(--magenta)]" /> <b>Del</b>-ikonet nederst i Safari</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 border border-[var(--magenta)]/50 flex items-center justify-center text-[var(--magenta)] text-xs">2</span>
                <span>Bla ned og velg <b>«Legg til på Hjem-skjerm»</b></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 border border-[var(--magenta)]/50 flex items-center justify-center text-[var(--magenta)] text-xs">3</span>
                <span>Trykk <b>«Legg til»</b> — appen dukker opp som ikon på skjermen din</span>
              </li>
            </ol>
            <button
              onClick={() => { setShowIOSHint(false); dismiss(); }}
              className="mt-6 w-full bg-[var(--magenta)] text-white py-3 text-sm font-meta tracking-[0.15em] hover:opacity-90 transition"
              data-testid="pwa-ios-hint-close"
            >
              LUKK
            </button>
          </div>
        </div>
      )}
    </>
  );
}
